import { StorageInterface } from '../stores/childNavStores';
import { AdminResponseDto, ProfileAdminResponses, AdminResponseSearchCriteria, AdminSubmitResponseDto } from '../types/AdminResponseDto';

/**
 * AdminResponseStorage - Manages local storage of completed assessment responses
 * 
 * Storage Structure:
 * - Key: admin_responses_{profileId}
 * - Value: ProfileAdminResponses containing array of AdminResponseDto
 * 
 * Features:
 * - Multiple responses per profile
 * - Search and filter capabilities
 * - Automatic timestamp tracking
 * - Response deduplication by RecId
 */
export class AdminResponseStorage {
  private storage: StorageInterface;
  private readonly keyPrefix = 'admin_responses_';

  constructor(storage: StorageInterface) {
    this.storage = storage;
  }

  /**
   * Save an admin response for a profile
   * Automatically handles deduplication based on RecId
   */
  async saveAdminResponse(profileId: string, adminResponse: AdminResponseDto): Promise<boolean> {
    try {
      console.log('💾 AdminResponseStorage: Saving admin response:', {
        profileId,
        recId: adminResponse.recId,
        recSurvey: adminResponse.recSurvey,
        recAlias: adminResponse.recAlias,
        responseDataCount: adminResponse.responseData.length
      });

      // Set the ID field to profileId for consistency
      adminResponse.id = profileId;

      // Get existing responses for this profile
      const existingResponses = await this.getProfileResponses(profileId);
      
      // Remove any existing response that matches this one (deduplication)
      // Strategy: match on recId if both have one; otherwise match on id (profileId) + recUTM
      const normalizeUTM = (ts: string) => (ts || '').replace(/\.\d{3}Z?$/, 'Z');
      const filteredResponses = existingResponses.responses.filter(response => {
        if (response.recId && adminResponse.recId) {
          // Both have recId — deduplicate on recId
          return response.recId !== adminResponse.recId;
        }
        // One or both missing recId — deduplicate on profileId + recUTM
        const sameProfile = response.id === adminResponse.id;
        const sameUTM = normalizeUTM(response.recUTM) === normalizeUTM(adminResponse.recUTM);
        return !(sameProfile && sameUTM);
      });

      // Add the new response
      filteredResponses.push(adminResponse);

      // Sort by date (most recent first)
      filteredResponses.sort((a, b) => new Date(b.recDate).getTime() - new Date(a.recDate).getTime());

      // Create updated profile responses
      const profileResponses: ProfileAdminResponses = {
        profileId,
        responses: filteredResponses,
        lastUpdated: new Date().toISOString()
      };

      // Save to storage
      const storageKey = this.getStorageKey(profileId);
      await this.storage.setItem(storageKey, JSON.stringify(profileResponses));

      console.log('✅ AdminResponseStorage: Admin response saved successfully:', {
        profileId,
        recId: adminResponse.recId,
        totalResponses: filteredResponses.length,
        storageKey
      });

      return true;
    } catch (error) {
      console.error('❌ AdminResponseStorage: Error saving admin response:', error);
      return false;
    }
  }

  /**
   * Get all responses for a specific profile
   */
  async getProfileResponses(profileId: string): Promise<ProfileAdminResponses> {
    try {
      const storageKey = this.getStorageKey(profileId);
      const storedData = await this.storage.getItem(storageKey);

      if (storedData) {
        const profileResponses: ProfileAdminResponses = JSON.parse(storedData);
        console.log('📖 AdminResponseStorage: Retrieved profile responses:', {
          profileId,
          responseCount: profileResponses.responses.length,
          lastUpdated: profileResponses.lastUpdated
        });
        return profileResponses;
      }

      // Return empty structure if no responses found
      const emptyResponses: ProfileAdminResponses = {
        profileId,
        responses: [],
        lastUpdated: new Date().toISOString()
      };

      console.log('📖 AdminResponseStorage: No responses found for profile:', profileId);
      return emptyResponses;
    } catch (error) {
      console.error('❌ AdminResponseStorage: Error retrieving profile responses:', error);
      // Return empty structure on error
      return {
        profileId,
        responses: [],
        lastUpdated: new Date().toISOString()
      };
    }
  }

  /**
   * Search responses across criteria
   */
  async searchResponses(criteria: AdminResponseSearchCriteria): Promise<AdminResponseDto[]> {
    try {
      console.log('🔍 AdminResponseStorage: Searching responses with criteria:', criteria);

      let allResponses: AdminResponseDto[] = [];

      if (criteria.profileId) {
        // Search specific profile
        const profileResponses = await this.getProfileResponses(criteria.profileId);
        allResponses = profileResponses.responses;
      } else {
        // Search all profiles (expensive operation)
        console.warn('⚠️ AdminResponseStorage: Searching all profiles - this may be slow');
        // Note: This would require listing all storage keys, which isn't supported by all storage implementations
        // For now, require profileId in search criteria
        throw new Error('profileId is required in search criteria');
      }

      // Apply filters
      let filteredResponses = allResponses;

      if (criteria.recSurvey) {
        filteredResponses = filteredResponses.filter(response => 
          response.recSurvey === criteria.recSurvey
        );
      }

      if (criteria.recAlias) {
        filteredResponses = filteredResponses.filter(response => 
          response.recAlias === criteria.recAlias
        );
      }

      if (criteria.recStatus) {
        filteredResponses = filteredResponses.filter(response => 
          response.recStatus === criteria.recStatus
        );
      }

      if (criteria.dateFrom) {
        const fromDate = new Date(criteria.dateFrom);
        filteredResponses = filteredResponses.filter(response => 
          new Date(response.recDate) >= fromDate
        );
      }

      if (criteria.dateTo) {
        const toDate = new Date(criteria.dateTo);
        filteredResponses = filteredResponses.filter(response => 
          new Date(response.recDate) <= toDate
        );
      }

      console.log('✅ AdminResponseStorage: Search completed:', {
        totalFound: allResponses.length,
        filteredCount: filteredResponses.length,
        criteria
      });

      return filteredResponses;
    } catch (error) {
      console.error('❌ AdminResponseStorage: Error searching responses:', error);
      return [];
    }
  }

  /**
   * Get a specific response by RecId within a profile
   */
  async getResponseByRecId(profileId: string, recId: string): Promise<AdminResponseDto | null> {
    try {
      const profileResponses = await this.getProfileResponses(profileId);
      const response = profileResponses.responses.find(r => r.recId === recId);

      if (response) {
        console.log('📋 AdminResponseStorage: Retrieved response by RecId:', {
          profileId,
          recId,
          recSurvey: response.recSurvey
        });
        return response;
      }

      console.log('❓ AdminResponseStorage: Response not found:', { profileId, recId });
      return null;
    } catch (error) {
      console.error('❌ AdminResponseStorage: Error retrieving response by RecId:', error);
      return null;
    }
  }

  /**
   * Delete a specific response by RecId
   */
  async deleteResponse(profileId: string, recId: string): Promise<boolean> {
    try {
      console.log('🗑️ AdminResponseStorage: Deleting response:', { profileId, recId });

      const profileResponses = await this.getProfileResponses(profileId);
      const initialCount = profileResponses.responses.length;

      // Filter out the response to delete
      profileResponses.responses = profileResponses.responses.filter(
        response => response.recId !== recId
      );

      const finalCount = profileResponses.responses.length;

      if (initialCount === finalCount) {
        console.log('❓ AdminResponseStorage: Response not found for deletion:', { profileId, recId });
        return false;
      }

      // Update timestamp
      profileResponses.lastUpdated = new Date().toISOString();

      // Save updated responses
      const storageKey = this.getStorageKey(profileId);
      await this.storage.setItem(storageKey, JSON.stringify(profileResponses));

      console.log('✅ AdminResponseStorage: Response deleted successfully:', {
        profileId,
        recId,
        remainingResponses: finalCount
      });

      return true;
    } catch (error) {
      console.error('❌ AdminResponseStorage: Error deleting response:', error);
      return false;
    }
  }

  /**
   * Update an existing local response with the server-confirmed RecId and fields
   * after a successful backend submission.
   * @param profileId   Profile the response belongs to
   * @param localId     The recId that was stored locally (may be a temp "local_…" value)
   * @param serverResponse  The AdminSubmitResponseDto returned by the backend
   */
  async updateResponseRecId(
    profileId: string,
    localId: string,
    serverResponse: AdminSubmitResponseDto
  ): Promise<boolean> {
    try {
      console.log('🔄 AdminResponseStorage: Updating response with server RecId:', {
        profileId,
        localId,
        serverRecId: serverResponse.RecId
      });

      const profileResponses = await this.getProfileResponses(profileId);
      const index = profileResponses.responses.findIndex(r => r.recId === localId);

      if (index === -1) {
        console.warn('⚠️ AdminResponseStorage: Local response not found for RecId update:', { profileId, localId });
        return false;
      }

      // Overwrite the server-assigned fields on the existing record and advance
      // the status to SUBMIT_SUCCESS so consumers can distinguish locally-complete
      // records (ADMIN_COMPLETE) from those confirmed by the backend.
      profileResponses.responses[index] = {
        ...profileResponses.responses[index],
        recId:         serverResponse.RecId         || profileResponses.responses[index].recId,
        recUTM:        serverResponse.RecUTM        || profileResponses.responses[index].recUTM,
        id:            serverResponse.ID            || profileResponses.responses[index].id,
        recPatient:    serverResponse.RecPatient    || profileResponses.responses[index].recPatient,
        recAssessment: serverResponse.RecAssessment || profileResponses.responses[index].recAssessment,
        recStatus:     'SUBMIT_SUCCESS',
        recstate:      'SUBMIT_SUCCESS',
      };

      const storageKey = this.getStorageKey(profileId);
      await this.storage.setItem(storageKey, JSON.stringify({
        ...profileResponses,
        lastUpdated: new Date().toISOString()
      }));

      console.log('✅ AdminResponseStorage: Response updated with server RecId:', {
        profileId,
        localId,
        serverRecId: serverResponse.RecId
      });
      return true;
    } catch (error) {
      console.error('❌ AdminResponseStorage: Error updating response RecId:', error);
      return false;
    }
  }

  /**
   * Clear all responses for a profile
   */
  async clearProfileResponses(profileId: string): Promise<boolean> {
    try {
      console.log('🧹 AdminResponseStorage: Clearing all responses for profile:', profileId);

      const storageKey = this.getStorageKey(profileId);
      await this.storage.removeItem(storageKey);

      console.log('✅ AdminResponseStorage: All responses cleared for profile:', profileId);
      return true;
    } catch (error) {
      console.error('❌ AdminResponseStorage: Error clearing profile responses:', error);
      return false;
    }
  }

  /**
   * Get response statistics for a profile
   */
  async getProfileStatistics(profileId: string): Promise<{
    totalResponses: number;
    uniqueSurveys: number;
    dateRange: { earliest: string | null; latest: string | null };
    statusCounts: Record<string, number>;
  }> {
    try {
      const profileResponses = await this.getProfileResponses(profileId);
      const responses = profileResponses.responses;

      const uniqueSurveys = new Set(responses.map(r => r.recSurvey)).size;
      const statusCounts: Record<string, number> = {};

      let earliest: string | null = null;
      let latest: string | null = null;

      responses.forEach(response => {
        // Count statuses
        statusCounts[response.recStatus] = (statusCounts[response.recStatus] || 0) + 1;

        // Track date range
        const responseDate = response.recDate;
        if (!earliest || responseDate < earliest) {
          earliest = responseDate;
        }
        if (!latest || responseDate > latest) {
          latest = responseDate;
        }
      });

      const statistics = {
        totalResponses: responses.length,
        uniqueSurveys,
        dateRange: { earliest, latest },
        statusCounts
      };

      console.log('📊 AdminResponseStorage: Profile statistics:', {
        profileId,
        ...statistics
      });

      return statistics;
    } catch (error) {
      console.error('❌ AdminResponseStorage: Error calculating statistics:', error);
      return {
        totalResponses: 0,
        uniqueSurveys: 0,
        dateRange: { earliest: null, latest: null },
        statusCounts: {}
      };
    }
  }

  /**
   * Convert AdminStateDto to AdminResponseDto for storage
   */
  static createAdminResponseFromState(
    adminState: any, 
    profileId: string, 
    additionalMetadata: Partial<AdminResponseDto> = {}
  ): AdminResponseDto {
    const now = new Date();
    const currentDate = now.toISOString().split('T')[0]; // YYYY-MM-DD format

    const adminResponse: AdminResponseDto = {
      recId: adminState.recId || `${profileId}_${Date.now()}`,
      recStatus: adminState.status || 'COMPLETED',
      recDate: currentDate,
      recClient: additionalMetadata.recClient || '',
      recPractice: additionalMetadata.recPractice || '',
      recSite: additionalMetadata.recSite || '',
      recStation: additionalMetadata.recStation || '',
      recSN: adminState.segment || '1',
      recPeriod: additionalMetadata.recPeriod || '',
      recWeek: additionalMetadata.recWeek || '',
      recSurvey: adminState.sourceId || additionalMetadata.recSurvey || 'ASSESSMENT',
      recAssessment: adminState.sourceId || additionalMetadata.recAssessment || '',  // C# RecAssessment = SourceId
      surveybList: adminState.segmentList || additionalMetadata.surveybList || '',   // C# SurveybList   = SegmentList
      recAlias: additionalMetadata.recAlias || adminState.surveyAliasId || adminState.sourceId || 'ASSESSMENT',
      id: profileId,
      seq_Num: adminState.pageNum || '1',
      recstate: adminState.status || 'COMPLETED',
      recYear: now.getFullYear().toString(),
      recQtr: Math.ceil((now.getMonth() + 1) / 3).toString(),
      recMon: (now.getMonth() + 1).toString().padStart(2, '0'),
      recDay: now.getDate().toString().padStart(2, '0'),
      duration: additionalMetadata.duration || '0',
      recFirst: additionalMetadata.recFirst || 'N',
      recProvider: additionalMetadata.recProvider || '',
      recDepartment: additionalMetadata.recDepartment || '',
      recSpecialty: additionalMetadata.recSpecialty || '',
      recGroup: additionalMetadata.recGroup || '',
      recLTM: additionalMetadata.recLTM || '',
      recUTM: additionalMetadata.recUTM || '',
      id_RC: additionalMetadata.id_RC || '',
      recLang: additionalMetadata.recLang || '1',
      surveyAliasList: additionalMetadata.surveyAliasList || '',
      recPatient: additionalMetadata.recPatient || profileId,
      responseData: adminState.data?.map((dataItem: any) => ({
        name: dataItem.name,
        value: dataItem.value.toString()
      })) || [],
      ...additionalMetadata
    };

    console.log('🔄 AdminResponseStorage: Created AdminResponseDto from AdminState:', {
      recId: adminResponse.recId,
      profileId,
      recSurvey: adminResponse.recSurvey,
      responseDataCount: adminResponse.responseData.length
    });

    return adminResponse;
  }

  private getStorageKey(profileId: string): string {
    return `${this.keyPrefix}${profileId}`;
  }
}